<template>
  <div id="stepOne">
    <h3 v-if="isLoading">Getting your application...</h3>
    <b-form v-if="isLoaded">
      <b-form-group>
        <h2>Academic Information</h2>
        <br/>
        <p>Applicants must be/have been enrolled as a full-time high school, undergraduate, or graduate students for at least one semester between June 2023 and September 2024.</p>
        <p>Applicants must be 18 years of age or older (on or before 15 May 2024).</p>
      </b-form-group>
      <b-form-group label="University / School / Institution Name" label-for="schoolName">
        <b-form-input type="text" v-model="schoolName" id="schoolName"></b-form-input>
      </b-form-group>
      <b-form-group label="City" label-for="schoolCity">
        <b-form-input type="text" v-model="schoolCity" id="schoolCity"></b-form-input>
      </b-form-group>
      <b-form-group label="State" label-for="schoolState">
        <b-form-input type="text" v-model="schoolState" id="schoolState"></b-form-input>
      </b-form-group>
      <b-form-group label="Country" label-for="schoolCountry">
        <b-form-select id="schoolCountry" v-model="schoolCountry" :options="countries" class="mb-3">
        </b-form-select>
      </b-form-group>
      <div class="row">
        <div class="col col-md-3">
          <b-form-group label="Expected Graduation Month" label-for="expectedGraduationMonth">
            <multiselect class="mb-3" placeholder="Please Select" track-by="value" label="text" :value="getExpectedGraduationMonth" :options="months" :searchable="false" @input="updateExpectedGraduationMonth"/>
          </b-form-group>
        </div>
        <div class="col col-md-3">
          <b-form-group label="Expected Graduation Year" label-for="expectedGraduationYear">
            <b-form-select id="expectedGraduationYear" label="Year" v-model="expectedGraduationYear" :options="years" class="mb-3">
            </b-form-select>
          </b-form-group>
        </div>
      </div>
      <b-form-group v-if="proofOfEnrollmentExists" label="Proof of Enrollment">
        <div class="row dz-preview">
          <div class="dz-image col col-md-3">
            <img :alt="proofOfEnrollment.name" :src="getDataURL" />
          </div>
          <div class="dz-info col col-md-3">
            <div class="filename">Filename: {{ proofOfEnrollment.name }}</div>
            <div class="size">File Size: {{ proofOfEnrollmentFileSize }} MB</div>
            <button type="button" class="btn arrow-btn-primary btn-sm" @click="removeProofOfEnrollment">Remove File</button>
          </div>
        </div>
      </b-form-group>
      <b-form-group v-else label="Proof of Enrollment" description="Drag and Drop file to upload. (File Types: PDF, JPEG) (Max Size: 4MB)">
        <div class="dz">
          <vue-dropzone ref="proofOfEnrollment" id="proofOfEnrollment" :options="dropzoneOptions" @vdropzone-file-added="addedFile" @vdropzone-removed-file="removedFile" @vdropzone-sending="sendingEvent" @vdropzone-success="uploadSuccessful"/>
        </div>
      </b-form-group>
      <b-form-group>
        <h2>Professional References</h2>
        <p>References will be contacted after applications close.</p>
        <h3>First Professional Reference</h3>
      </b-form-group>
      <b-form-group label="Reference Type" label-for="firstReferenceType">
        <b-form-select id="firstReferenceType" v-model="firstReferenceType" :options="referenceTypes" class="mb-3">
        </b-form-select>
      </b-form-group>
      <b-form-group label="Reference First Name" label-for="firstReferenceFirstName">
        <b-form-input type="text" v-model="firstReferenceFirstName" id="firstReferenceFirstName"></b-form-input>
      </b-form-group>
      <b-form-group label="Reference Last Name" label-for="firstReferenceLastName">
        <b-form-input type="text" v-model="firstReferenceLastName" id="firstReferenceLastName"></b-form-input>
      </b-form-group>
      <b-form-group label="Reference Email" label-for="firstReferenceEmail">
        <b-form-input type="text" v-model="firstReferenceEmail" id="firstReferenceEmail"></b-form-input>
      </b-form-group>
      <b-form-group>
        <h3>Second Professional Reference</h3>
      </b-form-group>
      <b-form-group label="Reference Type" label-for="secondReferenceType">
        <b-form-select id="secondReferenceType" v-model="secondReferenceType" :options="referenceTypes" class="mb-3">
        </b-form-select>
      </b-form-group>
      <b-form-group label="Reference First Name" label-for="secondReferenceFirstName">
        <b-form-input type="text" v-model="secondReferenceFirstName" id="secondReferenceFirstName"></b-form-input>
      </b-form-group>
      <b-form-group label="Reference Last Name" label-for="secondReferenceLastName">
        <b-form-input type="text" v-model="secondReferenceLastName" id="secondReferenceLastName"></b-form-input>
      </b-form-group>
      <b-form-group label="Reference Email" label-for="secondReferenceEmail">
        <b-form-input type="text" v-model="secondReferenceEmail" id="secondReferenceEmail"></b-form-input>
      </b-form-group>
      <b-form-group>
        <h2>SIGGRAPH Experience</h2>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox v-model="haveAttended">I was an attendee at a SIGGRAPH North America conference before</b-form-checkbox>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox v-model="haveAttendedAsia">I was an attendee at a SIGGRAPH Asia conference before</b-form-checkbox>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox v-model="haveVolunteered">I was a Student Volunteer at a SIGGRAPH North America conference before</b-form-checkbox>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox v-model="haveVolunteeredAsia">I was a Student Volunteer at a SIGGRAPH Asia conference before</b-form-checkbox>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox v-model="haveNotAttended">I have never attended a SIGGRAPH Conference</b-form-checkbox>
      </b-form-group>
      <b-form-group v-if="haveVolunteered" label="Please select which years in which you have been a SIGGRAPH North America Student Volunteer." label-for="yearsVolunteered">
        <multiselect :options="previousSvYears" :value="getYearsVolunteered" :searchable="false" :multiple="true" :close-on-select="false" :allow-empty="true" :hide-selected="true" @input="updateYearsVolunteered">
          <template slot="tag" slot-scope="props"><span class="custom__tag"><span>{{ props.option }}</span><span class="custom__remove" @click="props.remove(props.option)">❌</span></span></template>
        </multiselect>
      </b-form-group>
      <b-form-group v-if="haveVolunteeredAsia" label="Please select which years in which you have been a SIGGRAPH Asia Student Volunteer." label-for="yearsVolunteered">
        <multiselect :options="previousSvYears" :value="getYearsVolunteeredAsia" :searchable="false" :multiple="true" :close-on-select="false" :allow-empty="true" :hide-selected="true" @input="updateYearsVolunteeredAsia">
          <template slot="tag" slot-scope="props"><span class="custom__tag"><span>{{ props.option }}</span><span class="custom__remove" @click="props.remove(props.option)">❌</span></span></template>
        </multiselect>
      </b-form-group>
      <!--
      <b-form-group>
        <h2>Areas of Interest</h2>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox v-model="interestDesign">Motion Graphics / Graphic Design</b-form-checkbox>
        <b-form-checkbox v-model="interestAnimation">2D / 3D Animation</b-form-checkbox>
        <b-form-checkbox v-model="interestMarketing">Marketing / Advertising / Media</b-form-checkbox>
        <b-form-checkbox v-model="interestProductionDesign">Production</b-form-checkbox>
        <b-form-checkbox v-model="interestTechnical">Technical (pipeline engineers, gameplay designers, graphics engineers, product managers, tech writers, etc.)</b-form-checkbox>
        <b-form-checkbox v-model="interestWeb">Web Technology (cloud services, web graphics engineers, web UI/UX, etc.)</b-form-checkbox>
        <b-form-checkbox v-model="interestGaming">Gaming</b-form-checkbox>
      </b-form-group>
      <b-form-group label="Other Interests - 25 words or less" label-for="otherInterests">
        <div class="row">
          <div class="col col-md-8">
            <b-form-textarea id="otherInterests" @input="wordCount(25)" v-model="otherInterests" :rows="3" :max-rows="6"/>
            <p :class="wordLimitClass">{{ wordCountText }}</p>
          </div>
        </div>
      </b-form-group>
      <b-form-group>
        <h2>Technical Knowledge</h2>
        <p>How would you rate your level of expertise in the following tools and technical areas?<br/> <strong> (1 - Never Used and 5 - Expert)</strong></p>
      </b-form-group>
      <b-form-group>
        <label for="knowledgeFinalCutPro">Final Cut Pro<span class='ml-2 required'>(required)</span></label>
        <b-form-radio-group id="knowledgeFinalCutPro" v-model="knowledgeFinalCutPro" name="knowledgeFinalCutPro" class="radio">
          <b-form-radio value="1">1</b-form-radio>
          <b-form-radio value="2">2</b-form-radio>
          <b-form-radio value="3">3</b-form-radio>
          <b-form-radio value="4">4</b-form-radio>
          <b-form-radio value="5">5</b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <b-form-group>
        <label for="knowledgeAdobePremiere">Adobe Premiere<span class='ml-2 required'>(required)</span></label>
        <b-form-radio-group id="knowledgeAdobePremiere" v-model="knowledgeAdobePremiere" name="knowledgeAdobePremiere" class="radio">
          <b-form-radio value="1">1</b-form-radio>
          <b-form-radio value="2">2</b-form-radio>
          <b-form-radio value="3">3</b-form-radio>
          <b-form-radio value="4">4</b-form-radio>
          <b-form-radio value="5">5</b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <b-form-group>
        <label for="knowledgeAdobeAfterEffects">Adobe After Effects<span class='ml-2 required'>(required)</span></label>
        <b-form-radio-group id="knowledgeAdobeAfterEffects" v-model="knowledgeAdobeAfterEffects" name="knowledgeAdobeAfterEffects" class="radio">
          <b-form-radio value="1">1</b-form-radio>
          <b-form-radio value="2">2</b-form-radio>
          <b-form-radio value="3">3</b-form-radio>
          <b-form-radio value="4">4</b-form-radio>
          <b-form-radio value="5">5</b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <b-form-group>
        <label for="knowledgeAdobePhotoshop">Adobe Photoshop<span class='ml-2 required'>(required)</span></label>
        <b-form-radio-group id="knowledgeAdobePhotoshop" v-model="knowledgeAdobePhotoshop" name="knowledgeAdobePhotoshop" class="radio">
          <b-form-radio value="1">1</b-form-radio>
          <b-form-radio value="2">2</b-form-radio>
          <b-form-radio value="3">3</b-form-radio>
          <b-form-radio value="4">4</b-form-radio>
          <b-form-radio value="5">5</b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <b-form-group>
        <label for="knowledgeAdobeIllustrator">Adobe Illustrator<span class='ml-2 required'>(required)</span></label>
        <b-form-radio-group id="knowledgeAdobeIllustrator" v-model="knowledgeAdobeIllustrator" name="knowledgeAdobeIllustrator" class="radio">
          <b-form-radio value="1">1</b-form-radio>
          <b-form-radio value="2">2</b-form-radio>
          <b-form-radio value="3">3</b-form-radio>
          <b-form-radio value="4">4</b-form-radio>
          <b-form-radio value="5">5</b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <b-form-group>
        <label for="knowledgeCanva">Canva<span class='ml-2 required'>(required)</span></label>
        <b-form-radio-group id="knowledgeCanva" v-model="knowledgeCanva" name="knowledgeCanva" class="radio">
          <b-form-radio value="1">1</b-form-radio>
          <b-form-radio value="2">2</b-form-radio>
          <b-form-radio value="3">3</b-form-radio>
          <b-form-radio value="4">4</b-form-radio>
          <b-form-radio value="5">5</b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <b-form-group>
        <label for="knowledgeClosedCaptioningVideos">Closed Captioning Videos<span class='ml-2 required'>(required)</span></label>
        <b-form-radio-group id="knowledgeClosedCaptioningVideos" v-model="knowledgeClosedCaptioningVideos" name="knowledgeClosedCaptioningVideos" class="radio">
          <b-form-radio value="1">1</b-form-radio>
          <b-form-radio value="2">2</b-form-radio>
          <b-form-radio value="3">3</b-form-radio>
          <b-form-radio value="4">4</b-form-radio>
          <b-form-radio value="5">5</b-form-radio>
        </b-form-radio-group>
      </b-form-group>

    -->

    <b-form-group >
      <button style="margin-top: 8px" type="submit" :disabled="disabled" class="btn arrow-btn-primary btn-sm float-sm-right" @click="nextStep">Save and Continue</button>
    </b-form-group>
  </b-form>
</div>
</template>

<script>
  import { countries, months, years, previousSvYears, referenceTypes } from '@/util/SelectLists'
  import vue2Dropzone from 'vue2-dropzone'
  import 'vue2-dropzone/dist/vue2Dropzone.min.css'
  import Multiselect from 'vue-multiselect'
  import { mapState, mapGetters } from 'vuex'
  import router from '@/router'
  import moment from 'moment'

  const generateVuexSetters = (fields) => {
    let computed = {}
    fields.forEach(field => {
      computed[field] = {
        get () {
          return this.$store.state.application.data[field]
        },
        set (value) {
          this.$store.commit('application/updateField', { name: field, value })
        }
      }
    })
    return computed
  }

  export default {
    name: 'AppStepOne',
    data () {
      return {
        countries: countries,
        months: months,
        years: years,
        previousSvYears: previousSvYears,
        referenceTypes: referenceTypes,
        dropzoneOptions: {
          acceptedFileTypes: [ 'image/jpg', 'image/jpeg', 'application/pdf' ],
          url: 'changeme',
          maxFiles: 1,
          method: 'PUT',
          autoProcessQueue: false,
          renameFile: file => {
            let filename = this.$store.state.user.profile.svid + '.' + file.type.substring(file.type.indexOf('/') + 1)
            return filename
          }
        },
        wordCountText: '25 words remaining',
        wordLimitClass: 'under-limit',
        disabled: false
      }
    },
    computed: {
      ...generateVuexSetters([
        'schoolName',
        'schoolCity',
        'schoolState',
        'schoolCountry',
        'closeProximity',
        'expectedGraduationMonth',
        'expectedGraduationYear',
        'interestAnimation',
        'interestProductionDesign',
        'interestTechnical',
        'interestWeb',
        'interestDesign',
        'interestMarketing',
        'interestGaming',
        'otherInterests',
        'haveNotAttended',
        'haveAttended',
        'haveVolunteered',
        'yearsVolunteered',
        'haveAttendedAsia',
        'haveVolunteeredAsia',
        'yearsVolunteeredAsia',
        'firstReferenceType',
        'firstReferenceFirstName',
        'firstReferenceLastName',
        'firstReferenceEmail',
        'secondReferenceType',
        'secondReferenceFirstName',
        'secondReferenceLastName',
        'secondReferenceEmail',
        'knowledgeFinalCutPro',
        'knowledgeAdobePremiere',
        'knowledgeAdobeAfterEffects',
        'knowledgeAdobePhotoshop',
        'knowledgeAdobeIllustrator',
        'knowledgeCanva',
        'knowledgeClosedCaptioningVideos'
      ]),
      proofOfEnrollment: {
        get () {
          return this.$store.state.application.data.proofOfEnrollment
        }
      },
      ...mapGetters('application', [
        'proofOfEnrollmentExists',
        'proofOfEnrollmentFileSize',
        'getDataURL',
        'getExpectedGraduationMonth',
        'getExpectedGraduationYear',
        'getYearsVolunteered',
        'getYearsVolunteeredAsia'
      ]),
      ...mapState('application', ['isLoaded', 'isLoading'])
    },
    methods: {
      addedFile (file) {
        if (file.size < 4000000) {
          this.$store.dispatch('application/getSignedUrl', { fileType: file.type }).then(url => {
            this.$refs.proofOfEnrollment.setOption('url', url)
            this.$refs.proofOfEnrollment.processQueue()
          })
        } else {
          // File is too big
          this.$refs.proofOfEnrollment.removeFile(file)
        }
      },
      removedFile (file) {
        if (file.size > 4000000) {
          this.$store.commit('error', { message: 'File size is too large. Please keep uploads under 4 MB' })
        }
      },
      sendingEvent (file, xhr) {
        var _send = xhr.send
        xhr.send = function () {
          _send.call(xhr, file)
        }
      },
      uploadSuccessful (file, response) {
        if (file.type === 'application/pdf') {
          file.dataURL = ''
        }
        this.$store.commit('application/fileUploaded', {
          name: file.upload.filename,
          type: file.type,
          dataURL: file.dataURL,
          size: file.size
        })
        this.$store.dispatch('application/save')
      },
      removeProofOfEnrollment (e) {
        this.$store.commit('application/removeProofOfEnrollment')
      },
      updateExpectedGraduationMonth (value) {
        this.$store.commit('application/updateField', { name: 'expectedGraduationMonth', value: value.value })
      },
      updateExpectedGraduationYear (value) {
        this.$store.commit('application/updateField', { name: 'expectedGraduationYear', value: value })
      },
      updateYearsVolunteered (value) {
        this.$store.commit('application/updateField', { name: 'yearsVolunteered', value: value })
      },
      updateYearsVolunteeredAsia (value) {
        this.$store.commit('application/updateField', { name: 'yearsVolunteeredAsia', value: value })
      },
      wordCount (limit) {
        let wordCount = this.otherInterests.split(/\s+/).length - 1
        let remaining = limit - wordCount
        this.wordCountText = (remaining < 0) ? (remaining * -1).toString() + ' words over the limit' : remaining.toString() + ' words remaining'
        this.wordLimitClass = (remaining < 0) ? 'over-limit' : 'under-limit'
      },
      nextStep (e) {
        e.preventDefault()
        this.disabled = true
        if (moment().tz('UTC').isAfter(this.$store.state.applicationEndDate)) {
          router.push('/dashboard')
        } else {
          this.$store.commit('application/updateStep', 2)
          this.$store.dispatch('application/save').then(() => {
            router.push({ name: 'stepTwo' })
            this.disabled = false
          }).catch(err => {
            console.log(err.message)
            this.disabled = false
          })
        }
      }
    },
    created: function () {
      if (!this.$store.state.application.isLoaded) {
        this.$store.dispatch('application/load').then(() => {
          if (this.$store.getters['pastTLAppDeadline'] && this.$store.state.application.teamLeader) {
            this.$store.commit('application/disableTeamLeader')
          }
          if (this.$store.state.application.completed) {
            router.push('/dashboard')
          } else if (this.$store.state.application.isNew) {
            this.$store.dispatch('application/save')
          } else if (this.$store.state.application.step !== 1) {
            router.push('/application/' + this.$store.state.application.step.toString())
          }
        })
      }
      if (this.$store.getters['pastTLAppDeadline'] && this.$store.state.application.teamLeader) {
        this.$store.commit('application/disableTeamLeader')
      }
      if (this.$store.state.application.completed) {
        router.push('/dashboard')
      }
    },
    components: {
      vueDropzone: vue2Dropzone,
      multiselect: Multiselect
    }
  }
</script>

<style lang="scss">
  @import '../../assets/scss/FileUploader.scss';
  @import '../../../node_modules/vue-multiselect/dist/vue-multiselect.min.css';
</style>



// WEBPACK FOOTER //
// src/components/application/StepOne.vue